import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '../../components/ui/Button.jsx';
import { contact } from '../../data/site.js';
import { projectStages } from '../../data/content.js';
import { getProject } from '../../data/projects.js';
import styles from './ContactForm.module.css';

/**
 * The Netlify form name. Netlify finds forms by scanning the deployed HTML
 * at build time — which works here only because every route is prerendered.
 * A client-rendered SPA would ship an empty <div id="root"> and Netlify
 * would find nothing to register.
 *
 * Changing this string creates a *new* form in the Netlify dashboard and
 * orphans the old submissions, so treat it as permanent.
 */
const FORM_NAME = 'project-inquiry';

const EMPTY = { name: '', phone: '', email: '', stage: 'exploring', message: '' };

/** Netlify expects urlencoded key=value pairs, not JSON. */
const encode = (data) =>
  Object.entries(data)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

/**
 * The project inquiry form.
 *
 * Submissions go to Netlify Forms, which emails them on. There is still no
 * server of our own — Netlify intercepts the POST at the CDN edge, so the
 * site stays a folder of static files.
 *
 * Three things keep this honest when something goes wrong:
 *
 *  - The fields are never cleared until a submission actually succeeds, so
 *    a failed send never eats what someone typed.
 *  - A failed send says so and falls back to the phone and email, rather
 *    than silently swallowing an inquiry.
 *  - Without JavaScript the form still posts natively — the hidden
 *    form-name input and data-netlify attribute are what make that work —
 *    so a broken bundle costs the visitor nothing.
 */
export function ContactForm() {
  const [values, setValues] = useState(EMPTY);
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [searchParams] = useSearchParams();
  const prefilled = useRef(false);
  const formRef = useRef(null);
  const scrolled = useRef(false);

  /* Arriving from a project page — /contact?project=<slug> — starts the
     message off for the visitor, so they can send an inquiry without
     having to describe which house they mean.
     
     Applied in an effect rather than as the initial state because the page
     is prerendered without a query string: seeding it up front would make
     the first client render disagree with the served HTML. Only the slug
     travels in the URL; the sentence is built here from the project's own
     address, so a hand-crafted link cannot put words on the page.
     
     Runs once. A visitor who clears the box and writes their own message
     should not have it overwritten. */
  useEffect(() => {
    const project = getProject(searchParams.get('project') ?? '');
    if (!project?.address) return undefined;

    /* Prefill once. Someone who clears the box and writes their own
       message must not have it overwritten by a later render. */
    if (!prefilled.current) {
      prefilled.current = true;
      setValues((current) => ({
        ...current,
        stage: project.enquiryStage ?? current.stage,
        message: `I am interested in ${project.address}!`,
      }));
    }

    /* Bring the form into view. Someone who has just pressed "ask about
       this home" has already decided to write; landing them at the top of
       the page and making them scroll past the masthead to find a form
       that is already half-filled is a step for no reason.

       Tracked separately from the prefill guard rather than sharing it.
       Under StrictMode this effect runs, is cleaned up, and runs again —
       and a single guard would let the first run schedule the scroll only
       for the cleanup to cancel it, then short-circuit the second run
       before it could reschedule. The scroll would silently never happen.

       Delayed because <ScrollToTop> sends every route change back to the
       top; this has to land after that or it is undone. Offset by the
       sticky header and the eyebrow above the form, so the "Project
       Inquiry" label stays clear of the header rather than hiding under it. */
    if (scrolled.current) return undefined;
    const timer = setTimeout(() => {
      const form = formRef.current;
      if (!form) return;
      scrolled.current = true;
      /* Scroll to the block wrapping the form, not the form itself: that
         block also holds the "Project Inquiry" eyebrow, and aiming at the
         form alone left the label hidden behind the sticky header. Taking
         the wrapper means this stays correct if the label's size changes,
         rather than depending on a hand-tuned offset. */
      const target = form.parentElement ?? form;
      const headerHeight =
        document.querySelector('header')?.getBoundingClientRect().height ?? 0;
      const top =
        target.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
      window.scrollTo({
        top: Math.max(top, 0),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
      });
    }, 60);
    return () => clearTimeout(timer);
  }, [searchParams]);

  const update = (field) => (e) =>
    setValues((current) => ({ ...current, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');

    // Send the human-readable label — a notification email reading
    // "Just exploring" is worth more than one reading "exploring".
    const stageLabel =
      projectStages.find((s) => s.value === values.stage)?.label ?? values.stage;

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': FORM_NAME,
          ...values,
          stage: stageLabel,
        }),
      });
      if (!response.ok) throw new Error(`Netlify returned ${response.status}`);
      setState('sent');
      setValues(EMPTY);
    } catch {
      setState('error');
    }
  };

  if (state === 'sent') {
    return (
      <div className={`${styles.sent} mt-4`} role="status">
        <p className={styles.sentHeading}>Thank you — your inquiry is on its way.</p>
        <p className={styles.sentBody}>
          We&rsquo;ll follow up within one business day. If it&rsquo;s urgent, call{' '}
          <a className="underlined" href={contact.phoneHref}>{contact.phone}</a>.
        </p>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className={`${styles.form} mt-4`}
      name={FORM_NAME}
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
      onSubmit={handleSubmit}
    >
      {/* Netlify needs this in the payload to know which form was posted. */}
      <input type="hidden" name="form-name" value={FORM_NAME} />

      {/* Spam trap: invisible to people, irresistible to bots. Anything that
          fills it in gets its submission dropped. aria-hidden and tabIndex
          keep it away from screen readers and the keyboard. */}
      <p className={styles.honeypot} aria-hidden="true">
        <label>
          Don&rsquo;t fill this out if you&rsquo;re human
          <input name="bot-field" tabIndex={-1} autoComplete="off" />
        </label>
      </p>

      <div className={styles.field}>
        <label htmlFor="name">Full Name</label>
        <input
          type="text" id="name" name="name" autoComplete="name" required
          value={values.name} onChange={update('name')}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="phone">Phone</label>
        <input
          type="tel" id="phone" name="phone" autoComplete="tel" required
          value={values.phone} onChange={update('phone')}
        />
      </div>

      <div className={`${styles.field} ${styles.full}`}>
        <label htmlFor="email">Email</label>
        <input
          type="email" id="email" name="email" autoComplete="email" required
          value={values.email} onChange={update('email')}
        />
      </div>

      <div className={`${styles.field} ${styles.full}`}>
        <label htmlFor="stage">Project Stage</label>
        <select id="stage" name="stage" value={values.stage} onChange={update('stage')}>
          {projectStages.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      <div className={`${styles.field} ${styles.full}`}>
        <label htmlFor="message">Tell us about your project</label>
        <textarea
          id="message" name="message" required
          placeholder="Lot location, timeline, budget range, must-haves..."
          value={values.message} onChange={update('message')}
        />
      </div>

      <div className={`${styles.field} ${styles.full}`}>
        <Button type="submit" disabled={state === 'sending'}>
          {state === 'sending' ? 'Sending…' : 'Send Inquiry'}
        </Button>
        <p className={`${styles.status} mt-3`} aria-live="polite">
          {state === 'error' ? (
            <>
              Something went wrong sending that — nothing was lost, so you can try again.
              If it keeps failing, reach us directly at{' '}
              <a className="underlined" href={contact.phoneHref}>{contact.phone}</a> or{' '}
              <a className="underlined" href={contact.emailHref}>{contact.email}</a>.
            </>
          ) : null}
        </p>
      </div>
    </form>
  );
}
